<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, RouterView } from 'vue-router';
import PermissionsPageHelpDrawerContent from '@/components/permissions/PermissionsPageHelpDrawerContent.vue';
import PcoPageHelpDrawerContent from '@/components/resourcesPco/PcoPageHelpDrawerContent.vue';
import { usePermissionsPageHelpStore } from '@/stores/permissionsPageHelpStore';
import { usePcoPageHelpStore } from '@/stores/pcoPageHelpStore';
import { useCustomizerStore } from '../../stores/customizer';
import Customizer from './customizer/Customizer.vue';
import HorizontalHeader from './horizontal-header/HorizontalHeader.vue';
import HorizontalSidebar from './horizontal-sidebar/HorizontalSidebar.vue';
import VerticalHeaderVue from './vertical-header/VerticalHeader.vue';
import VerticalSidebarVue from './vertical-sidebar/VerticalSidebar.vue';
import { defineAsyncComponent } from 'vue';

// Lazy DE PROPÓSITO: um import estático puxaria o ConsoleLayout (e o CSS do
// design system, Tailwind incluso) para o bundle de ENTRADA — carregado por
// todos os usuários, mesmo com a flag desligada. Async, o chunk só baixa para
// quem ligou o shell novo.
const ConsoleLayout = defineAsyncComponent(() => import('../console/ConsoleLayout.vue'));

/**
 * POC shadcn-vue (branch poc/designSystemShadcn): shell novo opt-in por
 * localStorage `hub_shell = 'console'`. Como o FullLayout é registrado como
 * componente de rota em VÁRIOS módulos do router, delegar aqui cobre o app
 * inteiro sem tocar em nenhuma rota. Rollback: remover a chave e recarregar
 * (botões de alternância nos dois headers).
 */
const useConsoleShell = localStorage.getItem('hub_shell') === 'console';

const customizer = useCustomizerStore();
const pcoPageHelp = usePcoPageHelpStore();
const permissionsPageHelp = usePermissionsPageHelpStore();
const route = useRoute();
/** Algumas rotas (ex.: Reembolso) forçam o layout "Completo" independente da preferência global. */
const isBoxed = computed(() => customizer.boxed && !route.meta.forceFullWidth);
</script>

<template>
    <!-- POC: shell novo (Corporate Console) quando a flag está ligada -->
    <ConsoleLayout v-if="useConsoleShell" />
    <!-----LTR LAYOUT------->
    <v-locale-provider v-else>
        <v-app
            :theme="customizer.actTheme"
            :class="[
                customizer.actTheme,
                customizer.mini_sidebar ? 'mini-sidebar' : '',
                customizer.setHorizontalLayout ? 'horizontalLayout' : 'verticalLayout',
                customizer.setBorderCard ? 'cardBordered' : ''
            ]"
            :dark="customizer.darkMode"
        >
            <!---Customizer location right side--->
            <v-navigation-drawer app temporary elevation="10" location="right" v-model="customizer.Customizer_drawer" width="400">
                <Customizer />
            </v-navigation-drawer>

            <!---Ajuda PCO: mesmo padrão do Customizer (drawer na raiz do v-app)--->
            <v-navigation-drawer app temporary elevation="10" location="right" v-model="pcoPageHelp.drawerOpen" width="400">
                <PcoPageHelpDrawerContent />
            </v-navigation-drawer>

            <!---Ajuda Permissões / Rotas: mesmo padrão do Customizer--->
            <v-navigation-drawer app temporary elevation="10" location="right" v-model="permissionsPageHelp.drawerOpen" width="400">
                <PermissionsPageHelpDrawerContent />
            </v-navigation-drawer>

            <!-- Vertical Layout Components -->
            <template v-if="!customizer.setHorizontalLayout">
                <VerticalSidebarVue />
                <div :class="isBoxed ? 'maxWidth' : 'full-header'">
                    <VerticalHeaderVue />
                </div>
            </template>

            <!-- Horizontal Layout Components -->
            <template v-else>
                <div :class="isBoxed ? 'maxWidth' : 'full-header'">
                    <HorizontalHeader />
                </div>
                <HorizontalSidebar />
            </template>

            <v-main>
                <div class="mb-3 hr-layout">
                    <v-container fluid class="page-wrapper px-sm-5 px-4 pt-12 rounded-xl">
                        <div class="">
                            <div :class="isBoxed ? 'maxWidth' : ''">
                                <RouterView />
                            </div>
                        </div>
                    </v-container>
                </div>
            </v-main>
        </v-app>
    </v-locale-provider>
</template>
